import React, { FC, useEffect, useState } from "react";
import { Add, Delete, ExpandMore } from "@mui/icons-material";
import { Button, Input } from "../../../components/ui";
import { TRuleObj } from "../../../types/eiskEngine";
import { useDispatch, useSelector } from "react-redux";
import { TRootState } from "../../../store";
import { setOpenIndex } from "../../../reducers/riskEngineSlice";

interface Props {
  fromMainAction?: boolean;
  rules: TRuleObj[];
  handleRuleChange: (
    index: number,
    field: keyof TRuleObj,
    value: string | number
  ) => void;
  handleAddRule: () => void;
  handleDeleteRule: (index: number) => void;
}

const RulesSection: FC<Props> = ({
  fromMainAction,
  rules,
  handleRuleChange,
  handleAddRule,
  handleDeleteRule,
}) => {
  const { ruleMode, selectedRuleIndex, openIndex } = useSelector(
    (state: TRootState) => ({
      ruleMode: state.riskEngineDetailsSlice.ruleMode,
      selectedRuleIndex: state.riskEngineDetailsSlice.selectedRuleIndex,
      openIndex: state.riskEngineDetailsSlice.openIndex,
    })
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      (ruleMode === "edit" || ruleMode === "delete") &&
      selectedRuleIndex !== null
    ) {
      const rule = rules.find(
        (r) =>
          (r.rule_id && r.rule_id === selectedRuleIndex) ||
          (r.temp_id && r.temp_id === selectedRuleIndex)
      );

      if (rule) {
        const ruleKey = rule.rule_id || rule.temp_id;

        if (ruleKey && !openIndex.includes(ruleKey)) {
          dispatch(setOpenIndex([...openIndex, ruleKey]));
        }
      }
    }
  }, [selectedRuleIndex, ruleMode, rules]);

  const toggleRule = (ruleKey: number) => {
    const currentOpen = openIndex;

    const newOpen = currentOpen.includes(ruleKey)
      ? currentOpen.filter((i) => i !== ruleKey)
      : [...currentOpen, ruleKey];

    dispatch(setOpenIndex(newOpen));
  };

  const visibleRules = rules
    .map((rule, index) => ({ ...rule, originalIndex: index }))
    .filter((rule) => rule.status !== 0);

  return (
    <div className="border br-10 overflow-hidden bg-white">
      <div className="flex justify-between items-center card_bg_head gap-2 font-semibold bg-oceangreen">
        <span className="font-semibold">Rules</span>

        <Button
          variant="outlined-primary"
          onClick={() => {
            handleAddRule();
          }}
        >
          <Add className="color-primary" /> Add Rule
        </Button>
      </div>

      <div className="card_space_18 space-y-4 w-full" id="scroll_style">
        {visibleRules.length > 0 ? (
          visibleRules.map((rule, i) => {
            const ruleKey =
              rule.rule_id && rule.rule_id !== 0 ? rule.rule_id : rule.temp_id;

            const open = ruleKey !== undefined && openIndex.includes(ruleKey);

            return (
              <div
                key={ruleKey}
                className={` br-10 px-4 py-2 transition-all ${
                  open &&
                  ruleMode === "delete" &&
                  rule.rule_id &&
                  rule.rule_id === selectedRuleIndex
                    ? "border-red bg-light-red"
                    : "border bg-gray"
                } `}
              >
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => {
                    if (ruleKey) {
                      toggleRule(ruleKey);
                    }
                  }}
                >
                  <span className="font-semibold color-sub-text fs-14">
                    Rule {i + 1}{" "}
                    {rule.rule_name !== "" ? `(${rule.rule_name})` : ""}
                    {rule.score !== "" ? `(Score - ${rule.score})` : ""}
                  </span>

                  <div className="flex items-center gap-2">
                    {!(
                      open &&
                      ruleMode === "delete" &&
                      rule.rule_id &&
                      rule.rule_id === selectedRuleIndex
                    ) && (
                      <div
                        className="ml-2 bg-light-red p-2 rounded-full color-red cursor-pointer flex items-center justify-center w-[25px] h-[25px]"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (ruleKey) {
                            handleDeleteRule(ruleKey);
                          }
                        }}
                      >
                        <Delete style={{ fontSize: 16 }} />
                      </div>
                    )}

                    <div className="bg-white br-100 p-[2px]">
                      <ExpandMore
                        style={{
                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "0.3s",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {open && (
                  <div className="mt-2 space-y-2">
                    <Input
                      label="Condition"
                      type="text"
                      required
                      value={rule.rule_name}
                      onChange={(e) =>
                        handleRuleChange(
                          rule.originalIndex,
                          "rule_name",
                          e.target.value
                        )
                      }
                      palceholder="Define the condition (e.g., value > 700)"
                    />

                    <div className="flex gap-4">
                      <Input
                        label="Score Impact"
                        type="number"
                        value={rule.score}
                        onChange={(e) => {
                          const value = e.target.value;

                          const updatedVal = value.replace(/\D/g, "");

                          handleRuleChange(
                            rule.originalIndex,
                            "score",
                            updatedVal === "" ? "" : Number(updatedVal)
                          );
                        }}
                        palceholder="Points"
                        className="flex-1"
                        onFocus={(e) => {
                          e.target.value === "0" &&
                            handleRuleChange(rule.originalIndex, "score", "");
                        }}
                      />

                      {/* <Input
                        label="Description"
                        type="text"
                        required
                        value={rule.description}
                        onChange={(e) =>
                          handleRuleChange(index, "description", e.target.value)
                        }
                        palceholder="Enter description"
                        className="flex-[2]"
                      /> */}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <span className="text-center flex justify-center">
            No Rules Found
          </span>
        )}
      </div>
    </div>
  );
};

export default RulesSection;
